import InboxChatTextFieldComponent from "@/components/LazoInboxChatComponents/Inbox-chat-components/InboxChatTextFieldComponent";
import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import { useResponsive } from "@/hooks/useResponsive";
import { LazoChatConversationShell } from "@/models/ai-chat/LazoChatConversation";
import { LazoChatTypingDTO } from "@/models/ai-chat/LazoChatTyping";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, IconButton, styled, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { MentionItem } from "react-mentions";
import { SendMessageFn } from "../ChatContainer";
import PortalChatBar from "./PortalChatBar";
import { usePortalChatContext } from "./PortalChatContext";
import PortalChatMessageList from "./PortalChatMessageList";

interface PortalChatViewProps {
  onChatSelect: (chat: LazoChatConversationShell) => void;
  userTyping: LazoChatTypingDTO;
  sendMessage: SendMessageFn;
  currentUserExternalId: string;
  setNewConversationMembers: Dispatch<SetStateAction<string[]>>;
  onBackToList?: () => void;
  showBackButton?: boolean;
}

export function PortalChatView({
  onChatSelect,
  sendMessage,
  userTyping,
  currentUserExternalId,
  setNewConversationMembers,
  onBackToList,
  showBackButton = false,
}: PortalChatViewProps) {
  const { t } = useTranslation("chat");
  const [openSettings, setOpenSettings] = useState<boolean>(false);
  const { isMobile } = useResponsive();

  // Used when creating conversation, shows a preview of a conversation
  // with the intended new members
  const [chatPreview, setChatPreview] = useState<LazoChatConversationShell | null>(null);

  const {
    loadingMessages,
    firstLoadMessages,
    messages,
    members,
    chats,
    setChats,
    selectedChat,
    fetchMoreMessages,
    userExternalIdToTenantNamesMap,
    internalChat,
    generalChat,
    setGeneralChat,
    chatChannel,
    setChatChannel,
    embedded,
  } = usePortalChatContext();

  // Provider-level read-only (embedded "Historial" tab) or the per-chat read-only flag both disable sending.
  const isReadOnly = embedded || !!selectedChat?.readOnly;

  function handleSendMessage(
    message: string,
    conversation: LazoChatConversationShell,
    mentions?: MentionItem[] | undefined,
    files?: File[] | undefined
  ) {
    // Creating conversation and conversation already exists, use preview
    if (chatPreview?.externalId) {
      conversation = chatPreview;
    }
    sendMessage(message, conversation, mentions, files);

    setChatPreview(null);
  }

  return (
    <StyledChatBox>
      {/* Mobile Back Button Header */}
      {showBackButton && isMobile && (
        <MobileHeader>
          <IconButton
            onClick={onBackToList}
            sx={{
              padding: "12px",
              color: Colors.ELEGANT_BLACK,
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: Colors.ELEGANT_BLACK,
            }}
          >
            Chat
          </Typography>
        </MobileHeader>
      )}
      {/* Chat Bar - Fixed at top */}
      <PortalChatBar
        setNewConversationMembers={setNewConversationMembers}
        setOpenSettings={setOpenSettings}
        conversation={selectedChat}
        onChatSelect={onChatSelect}
        chats={chats}
        setChatPreview={setChatPreview}
        members={members}
        isMobile={isMobile}
      />

      {/* Messages Area - Scrollable */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {firstLoadMessages && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
            <Spinner size={"2rem"} sx={{ color: Colors.LAZO_PURPLE }} />
          </Box>
        )}
        {!firstLoadMessages && (
          <PortalChatMessageList
            loadingMessages={loadingMessages}
            messages={messages}
            conversation={selectedChat || chatPreview}
            openSettings={openSettings}
            setOpenSettings={setOpenSettings}
            onChatSelect={onChatSelect}
            setChats={setChats}
            fetchMoreMessages={fetchMoreMessages}
            userExternalIdToTenantNamesMap={userExternalIdToTenantNamesMap}
          />
        )}
        {userTyping &&
          userTyping.conversation?.externalId == selectedChat?.externalId &&
          currentUserExternalId != userTyping.user.externalId && (
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "rgba(0, 0, 0, 0.54)",
                padding: "0.5rem",
                backgroundColor: Colors.ACCENT_F1F3FF,
              }}
            >
              <span style={{ fontSize: "0.9rem", color: "BLACK" }}>{userTyping.user.firstName} </span>
              is typing...
            </Typography>
          )}
      </Box>

      {/* Text Field - Fixed at bottom */}
      <ChatTextFieldContainer>
        {isReadOnly ? (
          <Typography
            sx={{
              textAlign: "center",
              fontWeight: 800,
              textDecoration: "underline",
            }}
          >
            {t("chatView.readOnlyChat")}
          </Typography>
        ) : (
          <InboxChatTextFieldComponent
            onChatSelect={onChatSelect}
            internalChat={internalChat}
            selectedChat={selectedChat}
            generalChat={generalChat}
            setGeneralChat={setGeneralChat}
            chatChannel={chatChannel}
            setChatChannel={setChatChannel}
            setChats={setChats}
            onSendMessage={handleSendMessage}
            members={members}
            loading={firstLoadMessages}
          />
        )}
      </ChatTextFieldContainer>
    </StyledChatBox>
  );
}

const StyledChatBox = styled(Box)({
  width: "100%",
  maxWidth: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden", // Changed from "auto" to "hidden" - no scroll on main container
  minHeight: 0, // Important for flex children
  position: "relative",
  "& .chat-box-header": {
    position: "relative",
    zIndex: 1,
  },
  "& .chat-box-body": {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    overflow: "auto",
    padding: "1rem",
    height: "100%",
  },
  "& .chat-box-footer": {
    position: "relative",
    zIndex: 1,
  },
});

const ChatTextFieldContainer = styled(Box)({
  padding: "0 1em 1em 1em",
  backgroundColor: Colors.ACCENT_F1F3FF,
});

const MobileHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  padding: "0 8px",
  height: "64px",
  backgroundColor: Colors.WHITE,
  borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  position: "sticky",
  top: 0,
  zIndex: 3,
  gap: "8px",
});

export default PortalChatView;
